import React, { Fragment, useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import OktaSignIn from "@okta/okta-signin-widget";
import {
  Box,
  Center,
  CenterSingle,
  Cover,
  Module,
  Spinner,
  withWindowSize,
  constants,
} from "@thecb/components";

import EunaSignInIcon from "@/images/euna-signin-icon.svg";

const Loading = () => (
  <Cover singleChild>
    <Box>
      <Cover minHeight="100%" singleChild>
        <Center intrinsic>
          <Box>
            <Spinner size="100" />
          </Box>
        </Center>
      </Cover>
    </Box>
  </Cover>
);

/**
 * This component runs the Okta authentication SDK effects and renders
 * the Okta login page widget.
 *
 * @param {object} props
 * @param {boolean} props.isCallback - comes from `apps/core/src/components/page/Page.js` and
 *   determines whether the currently rendered page is the result of a callback (e.g., from Okta)
 * @returns {JSX.Element}
 */
const LoginPage = ({
  isCallback,
  oidc,
  oktaSessionExists,
  requestCurrentUser,
  setOktaCredentialsProvider,
  setSessionExists,
}) => {
  const history = useHistory();
  const { authState, oktaAuth } = useOktaAuth();
  const widgetRef = useRef();
  const [isLoading, setIsLoading] = useState(true);

  // Starts the Okta authentication SDK
  useEffect(() => {
    const startOktaAuth = async () => await oktaAuth.start();
    const stopOktaAuth = async () => await oktaAuth.stop();

    startOktaAuth();

    return () => {
      stopOktaAuth();
    };
  }, [oktaSessionExists, oktaAuth]);

  // Logs in and, after logging in, retrieves the authenticated user session
  useEffect(() => {
    const getOktaSession = async () => {
      let session = await oktaAuth.session.get();
      let exists = await oktaAuth.session.exists();
      const credentialProvider = session?.idp?.type;
      if (exists && credentialProvider) {
        setOktaCredentialsProvider(credentialProvider);
      }
    };

    const checkSession = async () => {
      let exists = await oktaAuth.session.exists();
      let isAuthenticated = await oktaAuth.isAuthenticated();
      if (exists && !isAuthenticated) {
        await oktaAuth.token.getWithRedirect();
      } else if (!exists) {
        setIsLoading(false);
        setSessionExists(false);
      }
    };

    const handleOktaLogin = async () => {
      let exists = await oktaAuth.session.exists();
      let isAuthenticated = await oktaAuth.isAuthenticated();

      if (!exists) {
        oktaAuth.tokenManager.clear();
        setIsLoading(false);
        setSessionExists(false);
        return;
      }

      if (!oktaAuth.isLoginRedirect()) {
        // Trigger an initial authState change event when the app startup
        oktaAuth.authStateManager.updateAuthState();
      }

      if (authState?.isAuthenticated) {
        requestCurrentUser({
          oktaAuth,
          accessToken: authState.accessToken.accessToken,
        });
        getOktaSession();
      } else {
        if (exists && isAuthenticated) {
          oktaAuth.authStateManager.subscribe(() => {
            // handle emitted latest authState
            checkSession();
          });
        } else {
          // handle app startup and get the token
          checkSession();
        }
      }
    };

    handleOktaLogin();
  }, [
    authState,
    history,
    oktaAuth,
    requestCurrentUser,
    setOktaCredentialsProvider,
    setSessionExists,
  ]);

  // Renders the Okta sign in widget at the ref specified in the React component
  useEffect(() => {
    if (!widgetRef.current) return;

    const { issuer, clientId, redirectUri } = oidc;

    const widget = new OktaSignIn({
      issuer: issuer,
      clientId: clientId,
      redirectUri: window.location.origin + redirectUri,
      useClassicEngine: true,
      logo: EunaSignInIcon,
      features: {
        idpDiscovery: true,
      },
      idpDiscovery: {
        requestContext: `${window.location.origin}/login`,
      },
      colors: {
        brand: constants.colors.LUCKY_POINT,
      },
      transformUsername: (username, _operation) => {
        return username.toLowerCase();
      },
      i18n: {
        en: {
          "primaryauth.title":
            "Welcome to Revenue Management. Please sign in to continue.",
          "primaryauth.username.placeholder": "Email address",
          "oform.next": "Continue",
        },
      },
    });

    if (!isLoading) {
      widget.renderEl(
        { el: widgetRef.current },
        (res) => {
          oktaAuth.handleLoginRedirect(res.tokens);
        },
        (err) => {
          throw err;
        }
      );
    }
    return () => widget.remove();
  }, [isLoading, oidc, oktaAuth, widgetRef]);

  // Wraps the Okta sign in widget within a log in page.

  // Because the route is constantly changing and the page is constantly refreshing
  // during an active authentication flow, it is not possible to have the loading
  // spinner consistently run. When an authentication-related callback is detected,
  // nothing is rendered.
  return isCallback ? (
    <Fragment />
  ) : isLoading ? (
    <Loading />
  ) : (
    <Box
      extraStyles={`
        display: flex;
        flex-direction: column;
        height: 100%;
      `}
    >
      <CenterSingle
        backgroundColor="inherit"
        content={
          <Fragment>
            <Module padding="0" spacing="0" spacingBottom="1rem">
              <div ref={widgetRef} />
            </Module>
          </Fragment>
        }
      />
    </Box>
  );
};

export default withWindowSize(LoginPage);
